import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, FileZip, FilePdf, Image, Plus, Trash, Palette, Crop, Sparkle, Check, FloppyDisk, Shuffle } from "@phosphor-icons/react";
import { useI18n } from "../lib/i18n";
import { getCarousel, updateCarousel, regenerateSlide, listBrands } from "../lib/api";
import SlideCanvas from "../components/SlideCanvas";
import PropertiesPanel from "../components/PropertiesPanel";
import ContentPanel from "../components/ContentPanel";
import BrandSettings from "../components/BrandSettings";
import BrandQuickPositions from "../components/BrandQuickPositions";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { FORMATS, formatDims } from "../lib/formats";
import { exportSinglePNG, exportZIP, exportPDF } from "../lib/export";
import { toast } from "sonner";
import { motion } from "framer-motion";

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export default function Editor() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t, lang } = useI18n();
  const [carousel, setCarousel] = useState(null);
  const [brands, setBrands] = useState([]);
  const [selected, setSelected] = useState(0);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [brandOpen, setBrandOpen] = useState(false);
  const [formatMenuOpen, setFormatMenuOpen] = useState(false);
  const [brandMenuOpen, setBrandMenuOpen] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenPrompt, setRegenPrompt] = useState("");
  const [bundleBusy, setBundleBusy] = useState(false);
  const exportRefs = useRef({});

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    getCarousel(id).then(setCarousel).catch(() => {
      toast.error(lang === "el" ? "Δεν βρέθηκε" : "Not found");
      nav("/");
    });
    refreshBrands();
  }, [id, nav, lang]);

  const refreshBrands = async () => {
    try {
      const list = await listBrands();
      setBrands(list);
    } catch {
      setBrands([]);
    }
  };

  const setCarouselBrand = async (brandId) => {
    try {
      const updated = await updateCarousel(id, { brand_id: brandId || "" });
      setCarousel(updated);
      setBrandMenuOpen(false);
      toast.success(lang === "el" ? "Brand ενημερώθηκε" : "Brand updated");
    } catch {
      toast.error("Update failed");
    }
  };

  const activeBrand = React.useMemo(
    () => brands.find((b) => b.id === carousel?.brand_id) || null,
    [brands, carousel?.brand_id]
  );

  const saveSlides = useCallback(async (slides) => {
    setSaving(true);
    try {
      const updated = await updateCarousel(id, { slides });
      setCarousel(updated);
    } catch {
      toast.error("Save error");
    } finally {
      setSaving(false);
    }
  }, [id]);

  const saveFormat = useCallback(async (format) => {
    setSaving(true);
    try {
      const updated = await updateCarousel(id, { format });
      setCarousel(updated);
    } catch {
      toast.error("Save error");
    } finally {
      setSaving(false);
    }
  }, [id]);

  const updateSlide = (idx, next) => {
    const slides = carousel.slides.map((s, i) => i === idx ? next : s);
    setCarousel({ ...carousel, slides });
    clearTimeout(window.__csSaveTimer);
    window.__csSaveTimer = setTimeout(() => saveSlides(slides), 600);
  };

  const addSlide = () => {
    const slides = [...carousel.slides, {
      id: crypto.randomUUID(),
      index: carousel.slides.length,
      title: "New slide", body: "", kicker: "", cta: "",
      bg_color: "#F9F8F6", text_color: "#15251E", accent_color: "#244A36",
      text_position: "center", text_align: "left", font_weight: "semibold", font_size: "lg", font_family: "general_sans", effect: "none",
      image_url: "", image_opacity: 0.25, image_position: "background",
    }];
    setCarousel({ ...carousel, slides });
    saveSlides(slides);
    setSelected(slides.length - 1);
  };

  const removeSlide = (idx) => {
    if (carousel.slides.length <= 1) return;
    const slides = carousel.slides.filter((_, i) => i !== idx).map((s, i) => ({ ...s, index: i }));
    setCarousel({ ...carousel, slides });
    saveSlides(slides);
    setSelected(Math.max(0, idx - 1));
  };

  const applyCurrentStyleToAll = () => {
    const src = carousel.slides[selected];
    const visual = {
      bg_color: src.bg_color, text_color: src.text_color, accent_color: src.accent_color,
      text_position: src.text_position, text_align: src.text_align,
      font_weight: src.font_weight, font_size: src.font_size, font_family: src.font_family,
      effect: src.effect,
      image_url: src.image_url, image_position: src.image_position, image_opacity: src.image_opacity,
    };
    const slides = carousel.slides.map((s) => ({ ...s, ...visual }));
    setCarousel({ ...carousel, slides });
    saveSlides(slides);
    toast.success(lang === "el" ? "Εφαρμόστηκε σε όλα" : "Applied to all");
  };

  // Random style palette + font + effect combos (curated, not pure random — each combo
  // is harmonious and tested). Applied to ALL slides at once.
  const RANDOM_STYLES = [
    { bg: "#0B0B0B", text: "#FFFFFF", accent: "#FFEB00", font: "bebas_neue",   weight: "bold",     effect: "none",  name: "Hormozi Black" },
    { bg: "#1A1A1A", text: "#FFFFFF", accent: "#00D563", font: "satoshi",      weight: "bold",     effect: "lines", name: "Sales Forge" },
    { bg: "#FDE7D9", text: "#3A1F0E", accent: "#D4672E", font: "dm_sans",      weight: "bold",     effect: "grain", name: "Warm Lifestyle" },
    { bg: "#2D1B4E", text: "#FFFFFF", accent: "#FFD93D", font: "work_sans",    weight: "bold",     effect: "circle",name: "High Energy" },
    { bg: "#F5E6D3", text: "#2A2520", accent: "#B8860B", font: "playfair",     weight: "semibold", effect: "none",  name: "Cattoni Editorial" },
    { bg: "#FFD9E8", text: "#2A0E1F", accent: "#E03B82", font: "manrope",      weight: "semibold", effect: "grain", name: "Forleo Pink" },
    { bg: "#EDEAE3", text: "#1C1C1C", accent: "#C9A96E", font: "playfair",     weight: "medium",   effect: "none",  name: "Premium Walker" },
    { bg: "#0A0A0A", text: "#F5F5F5", accent: "#F05A28", font: "ibm_plex",     weight: "semibold", effect: "lines", name: "Chris Do Tech" },
    { bg: "#E8F3F0", text: "#0D3B2E", accent: "#2BA876", font: "manrope",      weight: "bold",     effect: "none",  name: "Clean Marketing" },
    { bg: "#1B2D3A", text: "#F0F4F7", accent: "#FF8C42", font: "space_grotesk", weight: "bold",    effect: "grain", name: "Tech Sunset" },
    { bg: "#FFFFFF", text: "#1A1A1A", accent: "#FF3D71", font: "archivo_black", weight: "bold",    effect: "circle",name: "Bold Pop" },
    { bg: "#F4EDE4", text: "#2D1810", accent: "#8B4513", font: "fraunces",     weight: "semibold", effect: "none",  name: "Soft Editorial" },
    { bg: "#0F1B2D", text: "#E8EEF7", accent: "#7BFFB2", font: "inter_tight",  weight: "bold",     effect: "lines", name: "Cyber Mint" },
    { bg: "#FFFFFF", text: "#15251E", accent: "#244A36", font: "general_sans", weight: "semibold", effect: "none",  name: "Carousel Studio Default" },
  ];
  const [lastRandomIdx, setLastRandomIdx] = useState(-1);

  const randomizeStyle = () => {
    let idx;
    // Don't repeat the same style twice in a row
    do {
      idx = Math.floor(Math.random() * RANDOM_STYLES.length);
    } while (idx === lastRandomIdx && RANDOM_STYLES.length > 1);
    setLastRandomIdx(idx);
    const r = RANDOM_STYLES[idx];
    const visual = {
      bg_color: r.bg,
      text_color: r.text,
      accent_color: r.accent,
      font_family: r.font,
      font_weight: r.weight,
      effect: r.effect,
    };
    const slides = carousel.slides.map((s) => ({ ...s, ...visual }));
    setCarousel({ ...carousel, slides });
    saveSlides(slides);
    toast.success(lang === "el" ? `🎲 ${r.name}` : `🎲 ${r.name}`);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const slides = [...carousel.slides];
    const oldIdx = slides.findIndex((s) => s.id === active.id);
    const newIdx = slides.findIndex((s) => s.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const moved = arrayMove(slides, oldIdx, newIdx).map((s, i) => ({ ...s, index: i }));
    setCarousel({ ...carousel, slides: moved });
    saveSlides(moved);
    // Keep selection on the moved slide
    if (selected === oldIdx) setSelected(newIdx);
    else if (oldIdx < selected && newIdx >= selected) setSelected(selected - 1);
    else if (oldIdx > selected && newIdx <= selected) setSelected(selected + 1);
  };

  const regenerateCurrent = async (prompt = "") => {
    setRegenerating(true);
    try {
      const updated = await regenerateSlide(id, selected, prompt || "");
      setCarousel(updated);
      setRegenOpen(false);
      setRegenPrompt("");
      toast.success(lang === "el" ? "Νέα εκδοχή" : "Regenerated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setRegenerating(false);
    }
  };

  const handleExport = async (type) => {
    if (!carousel) return;
    setExporting(type);
    try {
      const nodes = carousel.slides.map((_, i) => exportRefs.current[i]).filter(Boolean);
      const rawTitle = (carousel.title || "carousel").toLowerCase().replace(/[^a-z0-9]+/gi, "-").slice(0, 40).replace(/^-+|-+$/g, "");
      const safeTitle = rawTitle || "carousel";
      const fmt = carousel.format || "1x1";
      if (type === "png") {
        await exportSinglePNG(exportRefs.current[selected], `${safeTitle}-${String(selected + 1).padStart(2, "0")}.png`, fmt);
      } else if (type === "zip") {
        await exportZIP(nodes, safeTitle, fmt);
      } else if (type === "pdf") {
        await exportPDF(nodes, `${safeTitle}.pdf`, fmt);
      }
      toast.success(lang === "el" ? "Έτοιμο για λήψη" : "Downloaded");
    } catch (e) {
      console.error(e);
      toast.error("Export failed");
    } finally {
      setExporting(null);
    }
  };

  const handleBundle = async () => {
    if (!carousel) return;
    setBundleBusy(true);
    try {
      const nodes = carousel.slides.map((_, i) => exportRefs.current[i]).filter(Boolean);
      const rawTitle = (carousel.title || "carousel").toLowerCase().replace(/[^a-z0-9]+/gi, "-").slice(0, 40).replace(/^-+|-+$/g, "");
      const safeTitle = rawTitle || "carousel";
      const fmt = carousel.format || "1x1";
      const cap = (carousel.caption_short || carousel.caption_seo || "").trim();
      const longCap = (carousel.caption_seo || "").trim();
      const tags = (carousel.hashtags || []).map((h) => h.startsWith("#") ? h : `#${h}`).join(" ");
      const altHooks = (carousel.alt_hooks || []).filter(Boolean);
      const altCtas = (carousel.alt_ctas || []).filter(Boolean);
      const altTexts = (carousel.slides || []).map((s, i) => ({ i, alt: (s.alt_text || "").trim() })).filter((x) => x.alt);
      const lines = [];
      lines.push(`# ${carousel.title}`);
      lines.push("");
      if (cap) {
        lines.push("## CAPTION (short)");
        lines.push(cap);
        lines.push("");
      }
      if (longCap && longCap !== cap) {
        lines.push("## CAPTION (long / SEO)");
        lines.push(longCap);
        lines.push("");
      }
      if (tags) {
        lines.push("## HASHTAGS");
        lines.push(tags);
        lines.push("");
      }
      if (altTexts.length) {
        lines.push("## ALT TEXT (accessibility — paste each in IG slide alt-text field)");
        altTexts.forEach(({ i, alt }) => {
          lines.push(`Slide ${String(i + 1).padStart(2, "0")}: ${alt}`);
        });
        lines.push("");
      }
      if (altHooks.length) {
        lines.push("## ALTERNATE HOOKS");
        altHooks.forEach((h, i) => lines.push(`${i + 1}. ${h}`));
        lines.push("");
      }
      if (altCtas.length) {
        lines.push("## ALTERNATE CTAS");
        altCtas.forEach((c, i) => lines.push(`${i + 1}. ${c}`));
        lines.push("");
      }
      lines.push("—");
      lines.push("Generated by Carousel Studio · we2design");
      const captionText = lines.join("\n");
      // Build ZIP including caption.txt
      await exportZIP(nodes, safeTitle, fmt, captionText);
      // Try clipboard copy (best-effort) so user can paste straight to IG
      const clipboardPayload = tags ? `${cap}\n\n${tags}` : cap;
      if (clipboardPayload) {
        try {
          await navigator.clipboard.writeText(clipboardPayload);
        } catch {
          // Silent fallback — user still has caption.txt in ZIP
        }
      }
      toast.success(lang === "el"
        ? "ZIP κατέβηκε με caption.txt μέσα · Caption αντιγράφηκε"
        : "ZIP downloaded with caption.txt inside · Caption copied");
    } catch (e) {
      console.error(e);
      toast.error(lang === "el" ? "Σφάλμα εξαγωγής" : "Export failed");
    } finally {
      setBundleBusy(false);
    }
  };

  if (!carousel) {
    return (
      <div className="flex items-center justify-center h-[60vh] text-ink-soft" data-testid="editor-loading">
        <div className="animate-pulse">Loading…</div>
      </div>
    );
  }

  const currentSlide = carousel.slides[selected];
  const fmt = carousel.format || "1x1";
  const dims = formatDims(fmt);
  // Canvas preview size — maxHeight in viewport to avoid clipping for 9:16
  const canvasW = dims.h > dims.w ? Math.min(480, 520 * (dims.w / dims.h)) : 480;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr_320px] h-[calc(100vh-64px)] overflow-hidden" data-testid="editor">
      {/* Left thumbnails with drag */}
      <aside className="border-r border-line bg-white overflow-y-auto custom-scrollbar">
        <div className="p-5 flex items-center justify-between sticky top-0 bg-white/90 backdrop-blur z-10 border-b border-line">
          <button onClick={() => nav("/")} className="text-xs text-ink-soft hover:text-ink flex items-center gap-1.5" data-testid="editor-back">
            <ArrowLeft size={14} /> {t("back_dashboard")}
          </button>
          <div className="text-[10px] text-ink-soft tracking-wider uppercase">{saving ? "saving…" : "saved"}</div>
        </div>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={carousel.slides.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            <div className="p-4 space-y-3" data-testid="slide-thumbnails">
              {carousel.slides.map((s, i) => (
                <SortableThumb
                  key={s.id}
                  slide={s}
                  index={i}
                  active={selected === i}
                  canDelete={carousel.slides.length > 1}
                  onSelect={() => setSelected(i)}
                  onDelete={() => removeSlide(i)}
                />
              ))}
              <button
                onClick={addSlide}
                className="w-full py-3 rounded-lg border border-dashed border-line text-ink-soft hover:text-forest hover:border-forest transition text-xs flex items-center justify-center gap-1.5"
                data-testid="add-slide"
              >
                <Plus size={14} weight="bold" /> {t("add_slide")}
              </button>
            </div>
          </SortableContext>
        </DndContext>
      </aside>

      {/* Center canvas */}
      <main className="bg-cream-deep overflow-y-auto custom-scrollbar">
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-cream-deep/90 backdrop-blur border-b border-line" data-testid="editor-toolbar">
          <div className="min-w-0">
            <div className="kicker-label">{carousel.language === "el" ? "ΕΛΛΗΝΙΚΑ" : "ENGLISH"} · {carousel.slides.length} SLIDES · {fmt.toUpperCase()}</div>
            <div className="font-heading text-ink font-medium text-lg truncate max-w-[360px]">{carousel.title}</div>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            {/* Format menu */}
            <div className="relative" data-testid="format-menu">
              <button
                onClick={() => setFormatMenuOpen((x) => !x)}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-full bg-white border border-line hover:border-forest/50 hover:text-forest transition"
                data-testid="format-menu-toggle"
              >
                <Crop size={14} weight="duotone" /> {fmt}
              </button>
              {formatMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-60 bg-white border border-line rounded-xl shadow-lg p-1 z-20" data-testid="format-menu-items">
                  {Object.entries(FORMATS).map(([k, v]) => (
                    <button
                      key={k}
                      onClick={() => { saveFormat(k); setFormatMenuOpen(false); }}
                      className={`w-full text-left px-3 py-2 rounded-lg text-xs hover:bg-cream-warm transition flex items-center justify-between ${fmt === k ? "bg-forest/5 text-forest" : "text-ink"}`}
                      data-testid={`format-opt-${k}`}
                    >
                      <div>
                        <div className="font-medium">{k}</div>
                        <div className="text-[10px] text-ink-soft">{v.label}</div>
                      </div>
                      <div className="text-[10px] text-ink-soft">{v.w}×{v.h}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="relative" data-testid="brand-menu">
              <button
                onClick={() => setBrandMenuOpen((x) => !x)}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-full bg-white border border-line hover:border-forest/50 hover:text-forest transition max-w-[180px]"
                data-testid="brand-menu-toggle"
                title={lang === "el" ? "Επέλεξε brand" : "Select brand"}
              >
                <Palette size={14} weight="duotone" />
                <span className="truncate">{activeBrand ? (activeBrand.name || activeBrand.handle || "Brand") : (lang === "el" ? "Χωρίς brand" : "No brand")}</span>
              </button>
              {brandMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-white border border-line rounded-xl shadow-lg p-1 z-20 max-h-80 overflow-y-auto" data-testid="brand-menu-items">
                  <button
                    onClick={() => setCarouselBrand("")}
                    className={`w-full text-left px-3 py-2 rounded-lg text-xs hover:bg-cream-warm transition flex items-center justify-between ${!carousel.brand_id ? "bg-forest/5 text-forest" : "text-ink"}`}
                    data-testid="brand-opt-none"
                  >
                    <span className="font-medium">{lang === "el" ? "Χωρίς brand" : "No brand"}</span>
                  </button>
                  {brands.map((b) => (
                    <button
                      key={b.id}
                      onClick={() => setCarouselBrand(b.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-xs hover:bg-cream-warm transition flex items-center gap-2 ${carousel.brand_id === b.id ? "bg-forest/5 text-forest" : "text-ink"}`}
                      data-testid={`brand-opt-${b.id}`}
                    >
                      <span className="w-5 h-5 rounded-sm border border-line" style={{ background: b.brand_color || "#244A36" }} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{b.name || "Untitled"}</div>
                        {b.handle && <div className="text-[10px] text-ink-soft truncate">{b.handle}</div>}
                      </div>
                    </button>
                  ))}
                  <div className="border-t border-line mt-1 pt-1">
                    <button
                      onClick={() => { setBrandMenuOpen(false); setBrandOpen(true); }}
                      className="w-full text-left px-3 py-2 rounded-lg text-xs hover:bg-cream-warm text-forest flex items-center gap-2"
                      data-testid="brand-manage"
                    >
                      <Plus size={12} weight="bold" /> {lang === "el" ? "Διαχείριση brands" : "Manage brands"}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => setBrandOpen(true)}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-full bg-white border border-line hover:border-forest/50 hover:text-forest transition"
              data-testid="brand-open"
              title={lang === "el" ? "Διαχείριση brands (μέχρι 5)" : "Manage brands (up to 5)"}
            >
              <Palette size={14} weight="duotone" /> {lang === "el" ? "Brands" : "Brands"}
            </button>

            <BrandQuickPositions
              brand={activeBrand}
              onBrandUpdated={(b) => setBrands((prev) => prev.map((x) => x.id === b.id ? b : x))}
            />

            <button
              onClick={randomizeStyle}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-full bg-white border border-line hover:border-forest/50 hover:text-forest transition"
              data-testid="randomize-style"
              title={lang === "el" ? "Τυχαίο στυλ (color + font + effect)" : "Random style (color + font + effect)"}
            >
              <Shuffle size={14} weight="duotone" />
              {lang === "el" ? "Τυχαίο" : "Shuffle"}
            </button>

            <div className="h-6 w-px bg-line mx-1" />

            <button
              onClick={handleBundle}
              disabled={bundleBusy}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-full bg-white border border-forest/40 text-forest hover:bg-forest/5 transition disabled:opacity-60"
              data-testid="bundle-download-copy"
              title={lang === "el" ? "Κατέβασε ZIP + αντιγραφή caption" : "Download ZIP + copy caption"}
            >
              <FloppyDisk size={14} weight="duotone" />
              {bundleBusy ? "…" : (lang === "el" ? "ZIP + Caption" : "ZIP + Caption")}
            </button>

            <ExportButton onClick={() => handleExport("png")} loading={exporting === "png"} icon={<Image size={14} weight="duotone" />} label={t("download_png")} testid="export-png" />
            <ExportButton onClick={() => handleExport("zip")} loading={exporting === "zip"} icon={<FileZip size={14} weight="duotone" />} label={t("download_zip")} testid="export-zip" />
            <ExportButton onClick={() => handleExport("pdf")} loading={exporting === "pdf"} icon={<FilePdf size={14} weight="duotone" />} label={t("download_pdf")} testid="export-pdf" />
          </div>
        </div>

        <div className="flex items-center justify-center p-10 min-h-[70vh]">
          <motion.div key={`${selected}-${fmt}`} initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.25 }}>
            <SlideCanvas slide={currentSlide} index={selected} total={carousel.slides.length} size={canvasW} format={fmt} brand={activeBrand} />
          </motion.div>
        </div>

        <div className="px-6 pb-10">
          <div className="max-w-3xl mx-auto bg-white rounded-2xl border border-line canvas-shadow">
            <ContentPanel carousel={carousel} />
          </div>
        </div>
      </main>

      <aside className="border-l border-line bg-white overflow-y-auto custom-scrollbar">
        <PropertiesPanel
          slide={currentSlide}
          onChange={(s) => updateSlide(selected, s)}
          onRegenerate={() => setRegenOpen(true)}
          regenerating={regenerating}
          onApplyToAll={applyCurrentStyleToAll}
        />
      </aside>

      {/* Hidden full-size export canvases */}
      <div style={{ position: "fixed", left: "-99999px", top: 0, pointerEvents: "none" }} aria-hidden>
        {carousel.slides.map((s, i) => (
          <div key={s.id} ref={(el) => { if (el) exportRefs.current[i] = el; }}>
            <SlideCanvas slide={s} index={i} total={carousel.slides.length} size={dims.w} format={fmt} brand={activeBrand} exportMode />
          </div>
        ))}
      </div>

      <BrandSettings open={brandOpen} onOpenChange={setBrandOpen} onSaved={() => refreshBrands()} />

      <Dialog open={regenOpen} onOpenChange={(v) => { setRegenOpen(v); if (!v) setRegenPrompt(""); }}>
        <DialogContent className="bg-white border-line rounded-2xl max-w-lg" data-testid="regen-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Sparkle size={18} weight="duotone" className="text-forest" />
              {lang === "el" ? `Αναδημιουργία slide ${selected + 1}` : `Regenerate slide ${selected + 1}`}
            </DialogTitle>
            <DialogDescription className="text-sm text-ink-soft">
              {lang === "el"
                ? "Πες στο AI τι να αλλάξει (προαιρετικό). Αν αφήσεις το πεδίο κενό, θα ξαναγράψει από την αρχή με βάση τα υπόλοιπα slides."
                : "Tell the AI what to change (optional). If left empty, it will rewrite from scratch using context from the other slides."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <textarea
              value={regenPrompt}
              onChange={(e) => setRegenPrompt(e.target.value)}
              rows={4}
              placeholder={lang === "el"
                ? "π.χ. Κάντο πιο σύντομο · βάλε συγκεκριμένο νούμερο · πιο επείγον CTA · λιγότερο εμπορικό…"
                : "e.g. make it shorter · add a specific number · more urgent CTA · less salesy…"}
              className="cs-input w-full"
              data-testid="regen-prompt"
              autoFocus
            />

            <div className="flex flex-wrap gap-1.5" data-testid="regen-presets">
              {(lang === "el"
                ? ["πιο σύντομο", "πιο επείγον CTA", "βάλε νούμερο", "λιγότερο εμπορικό", "πιο τολμηρό hook"]
                : ["shorter", "more urgent CTA", "add a number", "less salesy", "bolder hook"]
              ).map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setRegenPrompt((p) => p ? `${p}; ${preset}` : preset)}
                  className="text-[11px] px-2.5 py-1 rounded-full border border-line text-ink-soft hover:border-forest hover:text-forest transition"
                  data-testid={`regen-preset-${preset.replace(/\s+/g, "-")}`}
                >
                  + {preset}
                </button>
              ))}
            </div>

            <div className="flex justify-between gap-2 pt-2">
              <button
                type="button"
                onClick={() => { setRegenOpen(false); setRegenPrompt(""); }}
                className="px-5 py-2.5 rounded-full text-sm text-ink-soft hover:text-ink"
                data-testid="regen-cancel"
              >
                {lang === "el" ? "Ακύρωση" : "Cancel"}
              </button>
              <button
                type="button"
                onClick={() => regenerateCurrent(regenPrompt.trim())}
                disabled={regenerating}
                className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-full text-sm bg-forest text-cream hover:bg-forest-dark font-medium disabled:opacity-60"
                data-testid="regen-submit"
              >
                <Sparkle size={14} weight="duotone" />
                {regenerating ? (lang === "el" ? "Γράφω…" : "Writing…") : (lang === "el" ? "Αναδημιουργία" : "Regenerate")}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SortableThumb({ slide, index, active, canDelete, onSelect, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: slide.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onSelect}
      className={`relative cursor-grab active:cursor-grabbing rounded-lg border transition group ${active ? "border-forest ring-2 ring-forest/30" : "border-line hover:border-forest/40"}`}
      data-testid={`thumb-${index}`}
    >
      <div className="flex items-center p-2 gap-3">
        <div className="text-[10px] font-semibold text-ink-soft w-6">{String(index + 1).padStart(2, "0")}</div>
        <div className="flex-1 overflow-hidden">
          <div className="aspect-square w-full rounded overflow-hidden">
            <MiniSlide slide={slide} />
          </div>
        </div>
      </div>
      {canDelete && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          onPointerDown={(e) => e.stopPropagation()}
          className="absolute top-1 right-1 p-1 rounded-full bg-white border border-line text-ink-soft hover:text-destructive opacity-0 group-hover:opacity-100 transition"
          data-testid={`thumb-delete-${index}`}
        >
          <Trash size={12} />
        </button>
      )}
    </div>
  );
}

function MiniSlide({ slide }) {
  return (
    <div className="w-full h-full flex items-center justify-center p-2 text-center"
      style={{ background: slide.bg_color || "#F9F8F6", color: slide.text_color || "#15251E" }}>
      <div className="font-heading font-semibold text-[10px] leading-[1.1] line-clamp-4">
        {slide.title || slide.kicker || "—"}
      </div>
    </div>
  );
}

function ExportButton({ onClick, loading, icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-full bg-forest text-cream hover:bg-forest-dark transition disabled:opacity-60"
      data-testid={testid}
    >
      {icon}
      {loading ? "…" : label}
    </button>
  );
}
